import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams, useMatch } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import DocumentViewer from '../../components/Documents/DocumentViewer';
import { WorkflowSignSignatureModal } from '../../components/Documents/modals/WorkflowSignSignatureModal';
import { useToast } from '../../components/UI/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../services/documentService';
import {
  documentSignatureService,
  SignatureRequestStatusCode,
  SignatureStatusCode,
  SignatureTypeCode,
  type DocumentSignatureDto,
  type DocumentSignerDto,
} from '../../services/documentSignatureService';
import type { DocumentDetailsDto, DocumentNode } from '../../types/documents';
import { isValidRouteDocumentId } from '../../utils/documentRouteUtils';

function mapDocumentDetailsToNode(
  documentDetails: DocumentDetailsDto,
  fallbackDocumentId: string
): DocumentNode {
  return {
    id: documentDetails.id || fallbackDocumentId,
    customerId: documentDetails.customerId,
    title: documentDetails.title,
    description: documentDetails.description || '',
    nodeType: documentDetails.nodeType as 'folder' | 'document' | 'file' | 'link' | 'edoc',
    parentDocumentId: documentDetails.parentDocumentId || null,
    level: 0,
    order: 0,
    children: [],
    countChild: 0,
    path: documentDetails.path,
    fullPath: documentDetails.path,
    status: documentDetails.status || 'edit',
    requiresSignature: documentDetails.requiresSignature,
    publicationExpiresAt: documentDetails.publicationExpiresAt,
    currentVersion: documentDetails.currentVersion,
    content: documentDetails.content,
    metadata: {
      fileName: documentDetails.content?.fileName || documentDetails.title,
      contentType: documentDetails.content?.contentType || '',
      size: documentDetails.content?.fileSize != null ? String(documentDetails.content.fileSize) : '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      author: '',
      lastModifiedBy: '',
      lastModifiedAt: new Date().toISOString(),
    },
  };
}

function findMySignerEntry(
  signature: DocumentSignatureDto | null,
  userId: string | undefined,
  userEmail: string | undefined
): DocumentSignerDto | null {
  if (!signature?.signers?.length || !userId) return null;
  const uid = userId.trim().toLowerCase();
  const em = (userEmail || '').trim().toLowerCase();
  for (const s of signature.signers) {
    const sid = s.userId?.trim().toLowerCase();
    if (sid && sid === uid) return s;
    const sem = s.email?.trim().toLowerCase();
    if (!sid && em && sem === em) return s;
  }
  return null;
}

function signerStatusInfo(status: number) {
  switch (status) {
    case SignatureStatusCode.Signed:
      return {
        icon: <CheckCircleIcon className="h-5 w-5 text-emerald-500" />,
        label: 'Assinado',
        badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      };
    case SignatureStatusCode.Rejected:
      return {
        icon: <XCircleIcon className="h-5 w-5 text-red-500" />,
        label: 'Recusado',
        badgeClass: 'bg-red-50 text-red-800 border-red-200',
      };
    default:
      return {
        icon: <ClockIcon className="h-5 w-5 text-amber-500" />,
        label: 'Pendente',
        badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
      };
  }
}

function signatureTypeLabel(type: number): string {
  switch (type) {
    case SignatureTypeCode.Electronic:
      return 'Eletrônica';
    case SignatureTypeCode.Handwritten:
      return 'Manuscrita';
    case SignatureTypeCode.Certificate:
      return 'Certificado A1';
    default:
      return 'Eletrônica';
  }
}

export const VisitorSignPage: React.FC = () => {
  const params = useParams<{ documentId: string }>();
  const inboxDocMatch = useMatch({ path: '/sign/inbox/:documentId', end: true });
  const rawDocumentId = inboxDocMatch?.params.documentId ?? params.documentId;
  const documentId =
    rawDocumentId != null && String(rawDocumentId).trim().length > 0
      ? String(rawDocumentId).trim()
      : undefined;
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [selectedNode, setSelectedNode] = useState<DocumentNode | null>(null);
  const [signature, setSignature] = useState<DocumentSignatureDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [retryingFinalPdf, setRetryingFinalPdf] = useState(false);
  const [retryingPartialPdf, setRetryingPartialPdf] = useState(false);

  const loadAll = useCallback(async () => {
    if (!documentId || !isValidRouteDocumentId(documentId)) {
      addToast('Link do documento inválido.', 'error');
      navigate('/sign/inbox', { replace: true });
      return;
    }
    setLoading(true);
    try {
      const [details, latestSig] = await Promise.all([
        documentService.getDocumentDetails(documentId),
        documentSignatureService.getLatestByDocument(documentId),
      ]);
      if (!details) {
        addToast('Documento não encontrado.', 'error');
        navigate('/sign/inbox', { replace: true });
        return;
      }
      setSelectedNode(mapDocumentDetailsToNode(details, documentId));
      setSignature(latestSig);
    } catch (err: unknown) {
      console.error(err);
      addToast('Não foi possível carregar o documento.', 'error');
      navigate('/sign/inbox', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [documentId, navigate, addToast]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const myEntry = findMySignerEntry(signature, user?.id, user?.email);
  const signatureRequestOpen =
    signature?.status === SignatureRequestStatusCode.InProgress ||
    String(signature?.status) === '1';
  const isPending =
    myEntry &&
    myEntry.status === SignatureStatusCode.Pending &&
    signatureRequestOpen;
  const awaitingDispatch =
    myEntry &&
    myEntry.status === SignatureStatusCode.Pending &&
    !signatureRequestOpen;
  const statusInfo = myEntry ? signerStatusInfo(myEntry.status) : null;

  const signedCount =
    signature?.signedCount ??
    signature?.signers?.filter((s) => s.status === SignatureStatusCode.Signed).length ??
    0;
  const totalSigners = signature?.totalSigners ?? signature?.signers?.length ?? 0;
  const showSignedProgress = Boolean(signature && totalSigners > 0 && signedCount > 0);
  const preferSignedDocument = signedCount > 0;
  const viewerSignatureRequestId =
    signature && preferSignedDocument ? signature.id : undefined;
  const viewerDocumentKey = [
    selectedNode?.id ?? '',
    viewerSignatureRequestId ?? 'original',
    String(signedCount),
    signature?.finalSignedDocumentAvailable ? 'final' : 'partial',
    signature?.finalSignedDocumentFailed ? 'failed' : '',
    signature?.partialSignedDocumentFailed ? 'partial-failed' : '',
    signature?.partialDocumentUpdatedAt ?? '',
    signature?.finalSignedDocumentAvailable ? 'final-ready' : '',
    signature?.completedAt ?? '',
  ].join('-');
  const canDownloadPartialPreview = Boolean(
    signature?.partialSignedDocumentAvailable &&
      (signatureRequestOpen ||
        signature?.finalSignedDocumentProcessing ||
        signature?.finalSignedDocumentFailed)
  );
  const canDownloadFinal = Boolean(
    signature?.finalSignedDocumentAvailable &&
      signature.status === SignatureRequestStatusCode.Completed
  );
  const isSignedDocumentProcessing = Boolean(
    signature?.finalSignedDocumentProcessing === true
  );
  const isSignedDocumentFailed = Boolean(signature?.finalSignedDocumentFailed === true);
  const isPartialPreviewFailed = Boolean(signature?.partialSignedDocumentFailed === true);
  const signedDocumentStatus = isSignedDocumentFailed
    ? 'failed'
    : isSignedDocumentProcessing
      ? 'processing'
      : signature?.finalSignedDocumentAvailable
        ? 'ready'
        : null;

  const handleDownloadPartialPreview = useCallback(async () => {
    if (!signature || !selectedNode) {
      return;
    }

    try {
      const fileName = selectedNode.metadata?.fileName || selectedNode.title || 'documento';
      const withoutExt = fileName.replace(/\.[^/.]+$/, '');
      await documentSignatureService.downloadPartial(
        signature.id,
        `${withoutExt}-parcial.pdf`
      );
      addToast('Download iniciado.', 'success');
    } catch {
      addToast('Não foi possível baixar a prévia parcial.', 'error');
    }
  }, [signature, selectedNode, addToast]);

  const handleDownloadFinal = useCallback(async () => {
    if (!signature || !selectedNode) {
      return;
    }

    try {
      const fileName = selectedNode.metadata?.fileName || selectedNode.title || 'documento';
      const withoutExt = fileName.replace(/\.[^/.]+$/, '');
      await documentSignatureService.downloadSignedFinalPdf(
        signature.id,
        `${withoutExt}-assinado.pdf`
      );
      addToast('Download iniciado.', 'success');
    } catch {
      addToast('Não foi possível baixar o documento assinado.', 'error');
    }
  }, [signature, selectedNode, addToast]);

  const handleRetryFinalPdf = useCallback(async () => {
    if (!signature) {
      return;
    }

    setRetryingFinalPdf(true);
    try {
      const updated = await documentSignatureService.retryFinalPdf(signature.id);
      setSignature(updated);
      addToast('Geração do documento assinado reiniciada.', 'success');
    } catch {
      addToast('Não foi possível reiniciar a geração do PDF.', 'error');
    } finally {
      setRetryingFinalPdf(false);
    }
  }, [signature, addToast]);

  const handleRetryPartialPdf = useCallback(async () => {
    if (!signature) {
      return;
    }

    setRetryingPartialPdf(true);
    try {
      const updated = await documentSignatureService.retryPartialPdf(signature.id);
      setSignature(updated);
      addToast('Geração da prévia com assinaturas reiniciada.', 'success');
    } catch {
      addToast('Não foi possível reiniciar a geração da prévia.', 'error');
    } finally {
      setRetryingPartialPdf(false);
    }
  }, [signature, addToast]);

  if (loading || !documentId || !isValidRouteDocumentId(documentId)) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <span className="text-sm">Carregando documento…</span>
        </div>
      </div>
    );
  }

  if (!selectedNode) {
    return null;
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link
          to="/sign/inbox"
          className="inline-flex items-center gap-1 font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-3.5 w-3.5" />
          Minhas assinaturas
        </Link>
        <span>/</span>
        <span className="truncate text-gray-400">{selectedNode.title}</span>
      </div>

      {/* Corpo */}
      <div className="flex min-h-0 flex-1 gap-5">
        {/* Viewer */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3">
            <DocumentTextIcon className="h-4 w-4 shrink-0 text-gray-400" />
            <span className="truncate text-sm font-medium text-gray-700">{selectedNode.title}</span>
          </div>
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <DocumentViewer
              key={viewerDocumentKey}
              document={selectedNode}
              signatureRequestId={viewerSignatureRequestId}
              preferSignedDocument={preferSignedDocument}
              partialSignedDocumentFailed={isPartialPreviewFailed}
              signedDocumentStatus={signedDocumentStatus}
              onRetryFinalPdf={
                isSignedDocumentFailed && !retryingFinalPdf
                  ? () => {
                      void handleRetryFinalPdf();
                    }
                  : undefined
              }
              onRetryPartialPdf={
                isPartialPreviewFailed && !retryingPartialPdf
                  ? () => {
                      void handleRetryPartialPdf();
                    }
                  : undefined
              }
              embedded
              onClose={() => navigate('/sign/inbox')}
            />
          </div>
        </div>

        {/* Painel de ação */}
        <aside className="flex w-72 shrink-0 flex-col gap-4">
          {/* Card de status */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Status da assinatura
            </p>

            {statusInfo ? (
              <div
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${statusInfo.badgeClass}`}
              >
                {statusInfo.icon}
                {statusInfo.label}
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                <ClockIcon className="h-4 w-4" />
                Sem registro de assinatura
              </div>
            )}

            {myEntry && (
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Tipo</dt>
                  <dd className="font-medium text-gray-700">
                    {signatureTypeLabel(myEntry.signatureType)}
                  </dd>
                </div>
                {myEntry.signedAt && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Assinado em</dt>
                    <dd className="font-medium text-gray-700">
                      {new Date(myEntry.signedAt).toLocaleString('pt-BR')}
                    </dd>
                  </div>
                )}
              </dl>
            )}

            {showSignedProgress && (
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">
                  {signedCount} de {totalSigners} assinatura
                  {totalSigners > 1 ? 's' : ''} coletada
                  {signedCount > 1 ? 's' : ''}
                </span>
              </div>
            )}

            {isPartialPreviewFailed && !isSignedDocumentFailed && !isSignedDocumentProcessing && (
              <div className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                <p>
                  Não foi possível atualizar a prévia com as assinaturas. Sua assinatura permanece
                  registrada.
                </p>
                <button
                  type="button"
                  disabled={retryingPartialPdf}
                  onClick={() => {
                    void handleRetryPartialPdf();
                  }}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                >
                  {retryingPartialPdf ? 'Reiniciando…' : 'Tentar prévia novamente'}
                </button>
              </div>
            )}

            {isSignedDocumentProcessing && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Gerando documento assinado… a visualização será atualizada em instantes.
              </div>
            )}

            {isSignedDocumentFailed && (
              <div className="mt-3 space-y-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                <p>
                  Não foi possível gerar o PDF consolidado. Sua assinatura permanece registrada.
                </p>
                <button
                  type="button"
                  disabled={retryingFinalPdf}
                  onClick={() => {
                    void handleRetryFinalPdf();
                  }}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {retryingFinalPdf ? 'Reiniciando…' : 'Tentar novamente'}
                </button>
              </div>
            )}
          </div>

          {/* CTA */}
          {isPending ? (
            <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-sm text-gray-600">
                Revise o documento ao lado e, quando estiver pronto, clique em <strong>Assinar</strong>.
              </p>
              <button
                type="button"
                onClick={() => setSignModalOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-95"
              >
                <PencilSquareIcon className="h-4 w-4" />
                Assinar documento
              </button>
              {canDownloadPartialPreview && signature && (
                <button
                  type="button"
                  onClick={() => {
                    void handleDownloadPartialPreview();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Baixar prévia ({signedCount} de {totalSigners})
                </button>
              )}
            </div>
          ) : awaitingDispatch ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
              <p className="text-sm text-amber-900">
                A solicitação de assinatura ainda não foi enviada. Você será notificado quando for a
                sua vez de assinar.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-5">
              <p className="text-sm text-gray-500">
                {myEntry
                  ? myEntry.status === SignatureStatusCode.Signed
                    ? 'Você já assinou este documento. Nenhuma ação adicional é necessária.'
                    : 'A assinatura foi recusada. Entre em contato com o remetente para mais informações.'
                  : 'Não encontramos um convite de assinatura associado à sua conta neste documento.'}
              </p>
              {canDownloadPartialPreview && signature && (
                <button
                  type="button"
                  onClick={() => {
                    void handleDownloadPartialPreview();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Baixar prévia ({signedCount} de {totalSigners})
                </button>
              )}
              {canDownloadFinal && signature && (
                <button
                  type="button"
                  onClick={() => {
                    void handleDownloadFinal();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Baixar documento final
                </button>
              )}
            </div>
          )}
        </aside>
      </div>

      {myEntry && isPending && (
        <WorkflowSignSignatureModal
          isOpen={signModalOpen}
          documentId={documentId}
          signer={myEntry}
          userEmail={user?.email ?? ''}
          inviteToken={inviteToken}
          onClose={() => setSignModalOpen(false)}
          onCompleted={async () => {
            setSignModalOpen(false);
            addToast('Assinatura registrada com sucesso!', 'success');

            if (!documentId) {
              return;
            }

            try {
              const immediate = await documentSignatureService.getLatestByDocument(documentId);
              if (immediate) {
                setSignature(immediate);
              }
            } catch {
              // polling abaixo
            }

            for (let attempt = 0; attempt < 20; attempt += 1) {
              await new Promise((resolve) => setTimeout(resolve, 2000));
              try {
                const latest = await documentSignatureService.getLatestByDocument(documentId);
                if (!latest) {
                  continue;
                }

                setSignature(latest);

                if (latest.finalSignedDocumentAvailable) {
                  break;
                }

                if (latest.finalSignedDocumentFailed) {
                  break;
                }

                if (latest.partialSignedDocumentFailed) {
                  break;
                }

                const isCompleted =
                  latest.status === SignatureRequestStatusCode.Completed ||
                  String(latest.status) === '2';

                if (isCompleted && latest.finalSignedDocumentProcessing) {
                  continue;
                }

                if (!isCompleted && latest.partialSignedDocumentAvailable) {
                  break;
                }
              } catch {
                break;
              }
            }

            try {
              const details = await documentService.getDocumentDetails(documentId);
              if (details) {
                setSelectedNode(mapDocumentDetailsToNode(details, documentId));
              }
            } catch {
              // ignore
            }
          }}
        />
      )}
    </div>
  );
};
